import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import SEOHead from "../components/common/SEOHead";

// Import custom anime character images
import virtualStreamersImg from "../assets/images/Anime Characters/Virtual Streamers.webp";
import live2dArtistsImg from "../assets/images/Anime Characters/Live2D Artists.webp";
import modelers3dImg from "../assets/images/Anime Characters/3D Modelers.webp";
import contentManagersImg from "../assets/images/Anime Characters/Content Managers.webp";
import communityModeratorsImg from "../assets/images/Anime Characters/Community Moderators.webp";
import technicalStaffImg from "../assets/images/Anime Characters/Technical Staff.webp";

const HomePremium = () => {
  const seoData = {
    title: "Livneu - Empowering the Next Generation of Virtual Stars",
    description:
      "Livneu is Sri Lanka's first professional VTuber agency under LEVELSTAIR — crafting digital personalities and producing unforgettable experiences for global audiences.",
    image: "https://livneu.levelstair.com/ShareImage.png",
    url: "https://livneu.levelstair.com",
    keywords:
      "VTuber agency, virtual talent management, Livneu agency, LEVELSTAIR, virtual entertainment, Live2D animation, 3D character modeling, virtual streaming, digital entertainment",
  };

  const talentRoles = [
    {
      title: "Virtual Streamers",
      description:
        "Passionate content creators ready to embrace virtual personas and engage with global audiences through premium streaming experiences.",
      image: virtualStreamersImg,
    },
    {
      title: "Live2D Artists",
      description:
        "Master artists who breathe life into virtual characters with cutting-edge Live2D rigging and dynamic animation techniques.",
      image: live2dArtistsImg,
    },
    {
      title: "3D Modelers",
      description:
        "Visionary creators who design and sculpt immersive 3D virtual environments and next-generation avatar technology.",
      image: modelers3dImg,
    },
    {
      title: "Content Managers",
      description:
        "Strategic coordinators who orchestrate content schedules, audience engagement, and brand partnerships for maximum impact.",
      image: contentManagersImg,
    },
    {
      title: "Community Moderators",
      description:
        "Community architects who foster vibrant fan communities and provide exceptional support to our growing talent network.",
      image: communityModeratorsImg,
    },
    {
      title: "Technical Staff",
      description:
        "Innovation engineers who power our streaming infrastructure, software integration, and technical excellence behind the scenes.",
      image: technicalStaffImg,
    },
  ];

  const agencyPillars = [
    {
      icon: "rocket-outline",
      title: "Professional Management",
      description:
        "Industry-leading talent representation and career development",
    },
    {
      icon: "brush-outline",
      title: "Top-Tier Avatar Design",
      description: "Cutting-edge character creation and visual storytelling",
    },
    {
      icon: "trending-up-outline",
      title: "Streaming Growth Strategies",
      description: "Data-driven audience growth and engagement optimization",
    },
    {
      icon: "storefront-outline",
      title: "Merchandising & Branding",
      description: "Premium brand development and merchandise strategies",
    },
    {
      icon: "globe-outline",
      title: "Global Audience Reach",
      description: "International market expansion and multilingual support",
    },
  ];

  return (
    <HelmetProvider>
      <div className="min-h-screen">
        <SEOHead seoData={seoData} />

        {/* Hero Section - Premium VTuber Agency */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-hero">
          {/* Kawaii Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-pastel-purple/10 rounded-full blur-3xl animate-pulse"></div>
            <div
              className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-pastel-blue/10 rounded-full blur-3xl animate-pulse"
              style={{ animationDelay: "2s" }}
            ></div>
            <div
              className="absolute top-1/2 left-1/2 w-32 h-32 bg-kawaii-mint/10 rounded-full blur-2xl animate-pulse"
              style={{ animationDelay: "4s" }}
            ></div>
          </div>

          <div className="container mx-auto px-6 relative z-10 pt-24">
            <div className="grid lg:grid-cols-2 gap-1 items-center min-h-[80vh]">
              {/* Left Side - Hero Content */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="text-left"
              >
                <motion.h1 className="text-5xl md:text-7xl lg:text-8xl font-title text-text-primary mb-6 leading-tight kawaii-glow">
                  Empowering the Next Generation of{" "}
                  <span className="block text-text-accent">
                    Virtual Stars
                  </span>
                </motion.h1>

                <motion.p
                  className="text-xl md:text-2xl text-text-secondary mb-8 leading-relaxed max-w-2xl font-body"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  Livneu is Sri Lanka's first professional VTuber agency under
                  LEVELSTAIR. We are crafting digital personalities and producing
                  unforgettable experiences for global audiences.
                </motion.p>

                {/* CTA Buttons */}
                <motion.div
                  className="flex flex-col sm:flex-row gap-6"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  <Link to="/apply">
                    <motion.button
                      whileHover={{
                        scale: 1.05,
                        boxShadow: "0 20px 40px rgba(151, 36, 233 0.3)",
                      }}
                      whileTap={{ scale: 0.95 }}
                      className="glass-button px-8 py-4 text-lg"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      Apply as Talent
                    </motion.button>
                  </Link>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-4 bg-white/10 backdrop-blur-sm text-black font-semibold text-lg rounded-full border border-white/20 hover:bg-white/20 transition-all duration-300"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    onClick={() =>
                      document
                        .getElementById("talent-showcase")
                        .scrollIntoView({ behavior: "smooth" })
                    }
                  >
                    Discover Our Roster
                  </motion.button>
                </motion.div>
              </motion.div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          >
            <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-purple-400 rounded-full mt-2"></div>
            </div>
          </motion.div>
        </section>

        {/* Agency Pillars Section */}
        <section className="py-20 px-6">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2
                className="text-4xl md:text-5xl font-bold text-black mb-6"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                What Sets Us Apart
              </h2>
              <p
                className="text-xl text-gray-700 max-w-3xl mx-auto"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Industry-leading expertise meets cutting-edge technology to
                create unparalleled virtual entertainment experiences
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {agencyPillars.map((pillar, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                  className="glass-card p-8 smooth-transition"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-blue-500 rounded-xl flex items-center justify-center mb-6">
                    <ion-icon
                      name={pillar.icon}
                      style={{ fontSize: "2rem", color: "white" }}
                    ></ion-icon>
                  </div>

                  <h3
                    className="text-xl font-bold text-black mb-4"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {pillar.title}
                  </h3>

                  <p
                    className="text-gray-700 leading-relaxed"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {pillar.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Talent Showcase Section */}
        <section id="talent-showcase" className="py-20 px-6">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2
                className="text-4xl md:text-5xl font-bold text-black mb-6"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Now Recruiting Virtual Stars
              </h2>
              <p
                className="text-xl text-gray-700 max-w-3xl mx-auto mb-4"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Join our elite roster of virtual talent and creators
              </p>
              <p
                className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-blue-500 bg-clip-text text-transparent"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Could this be you?
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
              {talentRoles.map((role, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  whileHover={{ y: -10, scale: 1.03 }}
                  className="glass-card p-6 smooth-transition group"
                >
                  <div className="relative w-auto h-70 mx-auto mb-6 rounded-xl overflow-hidden">
                    <img
                      src={role.image}
                      alt={role.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>

                  <h3
                    className="text-xl font-bold text-black mb-4 group-hover:text-purple-400 transition-colors"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {role.title}
                  </h3>

                  <p
                    className="text-gray-700 leading-relaxed"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {role.description}
                  </p>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-center"
            >
              <Link to="/apply">
                <motion.button
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0 20px 40px rgba(151, 36, 233 0.3)",
                  }}
                  whileTap={{ scale: 0.95 }}
                  className="glass-button px-12 py-4 text-lg"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Start Your Journey
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Technology Section */}
        <section className="py-20 px-6">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2
                className="text-4xl md:text-5xl font-bold text-black mb-6"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Powered by Innovation
              </h2>
              <p
                className="text-xl text-gray-700 max-w-3xl mx-auto"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                State-of-the-art technology stack driving the future of virtual
                entertainment
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {[
                {
                  icon: "eye-outline",
                  title: "Face Tracking",
                  desc: "Real-time facial motion capture",
                },
                {
                  icon: "cube-outline",
                  title: "3D Motion Capture",
                  desc: "Full-body movement tracking",
                },
                {
                  icon: "brush-outline",
                  title: "Custom Avatar Rigs",
                  desc: "Bespoke character systems",
                },
                {
                  icon: "desktop-outline",
                  title: "Streaming Tech",
                  desc: "Professional broadcast quality",
                },
              ].map((tech, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="text-center glass-card p-6 smooth-transition"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <ion-icon
                      name={tech.icon}
                      style={{ fontSize: "2rem", color: "white" }}
                    ></ion-icon>
                  </div>
                  <h3
                    className="text-lg font-bold text-black mb-2"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {tech.title}
                  </h3>
                  <p
                    className="text-gray-700 text-sm"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {tech.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Application CTA Block */}
        <section className="py-20 px-6 relative overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-400/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
          </div>

          <div className="container mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h2
                className="text-4xl md:text-6xl font-bold text-black mb-6 leading-tight"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                We're not just building careers.
                <span className="block bg-gradient-to-r from-purple-400 to-blue-500 bg-clip-text text-transparent">
                  We're building legends.
                </span>
              </h2>

              <p
                className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                The future is virtual. Your time is now. Are you ready to take
                the stage?
              </p>

              <Link to="/apply">
                <motion.button
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0 25px 50px rgba(151, 46, 240, 0.4)",
                  }}
                  whileTap={{ scale: 0.95 }}
                  className="glass-button px-12 py-5 text-xl relative overflow-hidden group"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  <span className="relative z-10">Apply Now</span>
                  <div className="absolute inset-0 bg-white/20 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                </motion.button>
              </Link>

              {/* Quick Requirements Preview */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6"
              >
                {[
                  { icon: "mic-outline", text: "Quality Microphone" },
                  { icon: "desktop-outline", text: "Streaming Setup" },
                  { icon: "language-outline", text: "Language Fluency" },
                  { icon: "calendar-outline", text: "Consistent Schedule" },
                ].map((req, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 text-gray-700"
                  >
                    <div className="w-8 h-8 bg-purple-400/20 rounded-lg flex items-center justify-center">
                      <ion-icon
                        name={req.icon}
                        style={{ fontSize: "1.2rem", color: "#9c2ef9" }}
                      ></ion-icon>
                    </div>
                    <span style={{ fontFamily: "'Inter', sans-serif" }}>
                      {req.text}
                    </span>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Community Section */}
        <section className="py-20 px-6">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2
                className="text-4xl md:text-5xl font-bold text-black mb-6"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Join Our Growing Community
              </h2>
              <p
                className="text-xl text-gray-700 max-w-3xl mx-auto"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Connect with fellow creators, fans, and industry professionals
                in the Livneu ecosystem
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Discord Card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="glass-card p-8 text-center group cursor-pointer smooth-transition"
                onClick={() =>
                  window.open("https://discord.com/invite/xauShxdx6u", "_blank")
                }
              >
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <ion-icon
                    name="logo-discord"
                    style={{ fontSize: "2.5rem", color: "white" }}
                  ></ion-icon>
                </div>

                <h3
                  className="text-2xl font-bold text-black mb-4"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Discord Community
                </h3>
                <p
                  className="text-gray-700 mb-6"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Join our vibrant Discord server for real-time updates,
                  community events, and direct interaction with our team.
                </p>
                <button
                  className="glass-button px-6 py-3"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Join Discord
                </button>
              </motion.div>

              {/* WhatsApp Card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="glass-card p-8 text-center group cursor-pointer smooth-transition"
                onClick={() =>
                  window.open(
                    "https://chat.whatsapp.com/HlqjZtbFtU6ISAEMYBxVRz",
                    "_blank"
                  )
                }
              >
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <ion-icon
                    name="logo-whatsapp"
                    style={{ fontSize: "2.5rem", color: "white" }}
                  ></ion-icon>
                </div>

                <h3
                  className="text-2xl font-bold text-black mb-4"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  WhatsApp Updates
                </h3>
                <p
                  className="text-gray-700 mb-6"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Get instant notifications about auditions, announcements, and
                  exclusive content directly to your phone.
                </p>
                <button
                  className="glass-button px-6 py-3"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Join WhatsApp
                </button>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </HelmetProvider>
  );
};

export default HomePremium;
